// ─── Domain types ─────────────────────────────────────────────────────────────

export type MessageType = 'ai' | 'human' | 'system' | 'tool' | string;

/** A single item inside a reasoning summary block */
export interface ReasoningSummaryItem {
  text?: string;
}

/** A reasoning content block (new trace format) */
export interface ReasoningContentBlock {
  type: 'reasoning';
  summary: ReasoningSummaryItem[];
}

/** A tool-call content block, e.g. file_search_call */
export interface ToolCallContentBlock {
  type: `${string}_call`;
  [key: string]: unknown;
}

/** Plain text content block */
export interface TextContentBlock {
  type: 'text';
  text: string;
}

/** Image URL content block */
export interface ImageUrlContentBlock {
  type: 'image_url';
  [key: string]: unknown;
}

/** Audio content block */
export interface AudioContentBlock {
  type: 'input_audio' | 'audio';
  [key: string]: unknown;
}

export interface ThinkingContentBlock {
  type: 'thinking';
  thinking: string;
}

export type ContentBlock =
  | ReasoningContentBlock
  | ThinkingContentBlock
  | ToolCallContentBlock
  | TextContentBlock
  | ImageUrlContentBlock
  | AudioContentBlock
  | { type: string; text?: string; [key: string]: unknown };

/** A resolved tool call with a name and its arguments */
export interface ToolCall {
  name: string;
  args: Record<string, unknown>;
}

/** A single message from the trace */
export interface TraceMessage {
  id: string;
  type: MessageType;
  content?: string | ContentBlock[] | { text?: string; [key: string]: unknown };
  tool_calls?: ToolCall[];
  additional_kwargs?: {
    reasoning?: ReasoningContentBlock | null;
    [key: string]: unknown;
  };
}

/** A row in the rendered trace table */
export interface TraceRow {
  type: MessageType;
  description: string;
}

// ─── HTML / Markdown helpers ──────────────────────────────────────────────────

const makeBold = (text: string, weight: number = 700): string =>
  `<span style="font-weight: ${weight};">${text}</span>`;

const escapeHTML = (str: string = ''): string =>
  String(str).replace(
    /[&<>"]/g,
    (c) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
      })[c],
  );

export const formatMarkdown = (text: string): string => {
  if (!text) return '';
  return escapeHTML(text)
    .replace(/\*\*(.*?)\*\*/g, (_, content) => makeBold(content, 500))
    .replace(/\*(?![*])(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>');
};

export const formatPlain = (text: string): string => {
  if (!text) return '';
  return escapeHTML(text).replace(/\n/g, '<br>');
};

// ─── Content block helpers ────────────────────────────────────────────────────

// NEW: detect tool-call blocks embedded into content (e.g. file_search_call)
export const isToolCallBlock = (obj: unknown): boolean => {
  if (!obj || typeof obj !== 'object') return false;
  const t = (obj as { type?: unknown }).type;
  return t != null && String(t).endsWith('_call');
};

// NEW: extract tool calls from content blocks (some traces store them inside `content` array)
export const extractToolCallsFromContent = (content: TraceMessage['content']): ToolCall[] => {
  if (!Array.isArray(content)) return [];

  const calls = [];
  for (const c of content) {
    if (!isToolCallBlock(c)) continue;

    const type = c.type != null ? String(c.type) : 'tool_call';
    const name = type.replace(/_call$/, '');

    // Put all fields (except `type`) into args so renderTools can show them (id/status/queries/etc.)
    const args = {};
    for (const [k, v] of Object.entries(c)) {
      if (k === 'type') continue;
      args[k] = v;
    }

    calls.push({ name, args });
  }
  return calls;
};

// NEW: extract reasoning from content blocks (new trace format)
export const extractReasoningFromContent = (
  content: TraceMessage['content'],
): ReasoningContentBlock | null => {
  if (!Array.isArray(content)) return null;

  const blocks = content as ContentBlock[];

  const summary: ReasoningSummaryItem[] = [];

  for (const block of blocks) {
    if (block == null || typeof block !== 'object') continue;

    if (block.type === 'reasoning' && Array.isArray((block as ReasoningContentBlock).summary)) {
      summary.push(...(block as ReasoningContentBlock).summary);
      continue;
    }

    if (block.type === 'thinking') {
      const text = (block as { thinking?: string }).thinking;
      if (typeof text === 'string' && text.trim()) {
        summary.push({ text });
      }
    }
  }

  if (summary.length === 0) return null;

  return { type: 'reasoning', summary };
};
// NEW: extract only visible text from content, avoiding dumping JSON/base64
export const extractVisibleTextContent = (content: TraceMessage['content']): string => {
  if (typeof content === 'string') return content;

  if (Array.isArray(content)) {
    return (content as ContentBlock[])
      .map((c) => {
        if (c == null) return '';
        if (typeof c === 'object') {
          if (c.type === 'reasoning' || c.type === 'thinking' || isToolCallBlock(c)) return '';
          if (c.type === 'text') return (c as TextContentBlock).text || '';
          if (c.type === 'image_url') return '[image]';
          if (c.type === 'input_audio' || c.type === 'audio') return '[audio]';
          if (typeof (c as { text?: unknown }).text === 'string')
            return (c as { text: string }).text;
          return '';
        }
        return typeof c === 'string' ? c : String(c);
      })
      .join(' ')
      .trim();
  }

  if (content && typeof content === 'object') {
    return (content as { text?: string }).text ?? JSON.stringify(content);
  }

  return '';
};

// ─── Render helpers (return HTML strings) ────────────────────────────────────
export const renderTools = (tools: ToolCall[]): string => {
  if (!Array.isArray(tools) || tools.length === 0) return '';

  return tools
    .map((tool) => {
      let toolBlock = `${makeBold('Tool name:', 500)} ${tool.name}<br>`;

      if (tool.args && typeof tool.args === 'object') {
        for (const [key, value] of Object.entries(tool.args)) {
          if (Array.isArray(value)) {
            toolBlock += `${makeBold(key, 500)}:<br>- ${value.map((v) => String(v)).join('<br>- ')}<br>`;
          } else {
            toolBlock += `${makeBold(key, 500)}: ${value}<br>`;
          }
        }
      } else if (tool.args != null) {
        toolBlock += `${tool.args}<br>`;
      }
      return toolBlock;
    })
    .join('<br>');
};

export const renderReasoning = (
  reasoningData: ReasoningContentBlock | null | undefined,
): string => {
  const summary = reasoningData?.summary;

  if (!Array.isArray(summary)) return '';

  const text = summary
    .map((item) => item.text || '')
    .join('\n')
    .trim();

  if (!text) return '';
  return `${makeBold('--- Reasoning ---', 500)}<br>${formatMarkdown(text)}`;
};

// ─── Message → HTML string ────────────────────────────────────────────────────
export const mapMessageContent = (message: TraceMessage, type: MessageType): string => {
  const toolBlock = renderTools([
    ...(message?.tool_calls || []),
    ...extractToolCallsFromContent(message?.content),
  ]);

  // NEW: support both old and new reasoning locations
  const reasoningSource =
    type === 'ai'
      ? message.additional_kwargs?.reasoning || extractReasoningFromContent(message.content)
      : null;

  const reasoningBlock = type === 'ai' ? renderReasoning(reasoningSource) : '';

  // NEW: show only visible assistant/user text (ignore reasoning blocks inside content)
  const rawContent = extractVisibleTextContent(message.content);

  const contentBlock =
    type === 'ai' || type === 'system' ? formatMarkdown(rawContent) : formatPlain(rawContent);

  return [toolBlock, contentBlock, reasoningBlock].filter(Boolean).join('<br><br>');
};

// ─── Trace builder ────────────────────────────────────────────────────────────
export const buildTrace = (traceMessages: TraceMessage[] = []): TraceRow[] =>
  traceMessages.map((msg) => ({
    type: msg.type,
    description: mapMessageContent(msg, msg.type),
  }));
